import React, { useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import {
  ArrowLeft,
  CalendarDays,
  CheckCircle2,
  Mail,
  MessageCircle,
  Phone,
  ShieldCheck,
} from "lucide-react";
import { Button } from "./ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "./ui/card";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { trackCRMEvent } from "../lib/crmTracking";
import { submitLeadContact } from "../lib/leadCapture";

const CONTACT_WHATSAPP = "5515996594981";
const GOOGLE_CALENDAR_LINK = "";
const GOOGLE_CALENDAR_EMBED_URL =
  "https://calendar.google.com/calendar/embed?src=e4e6370b0b63e64482fb6b6ef02fd8a75697c081a9301438f622564d8965a3c3%40group.calendar.google.com&ctz=America%2FSao_Paulo";

const benefits = [
  "Renda recorrente com carteira própria",
  "Sistema validado e suporte direto",
  "Entrada simples e comercial objetiva",
];

const setMetaDescription = (content) => {
  if (typeof document === "undefined") {
    return;
  }

  let meta = document.querySelector('meta[name="description"]');

  if (!meta) {
    meta = document.createElement("meta");
    meta.setAttribute("name", "description");
    document.head.appendChild(meta);
  }

  meta.setAttribute("content", content);
};

const ContactPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    whatsapp: "",
  });
  const [submitted, setSubmitted] = useState(false);

  const whatsappMessage = useMemo(() => {
    const lines = [
      "Ola! Tenho interesse em começar como revendedor.",
      `Nome: ${formData.name || "-"}`,
      `E-mail: ${formData.email || "-"}`,
      `WhatsApp: ${formData.whatsapp || "-"}`,
    ];

    return encodeURIComponent(lines.join("\n"));
  }, [formData]);

  useEffect(() => {
    document.title = "Comece Agora | Revenda Gateway";
    setMetaDescription(
      "Preencha seus dados e fale com a equipe Gateway para começar como revendedor de software."
    );

    trackCRMEvent({
      eventType: "page_view",
      eventName: "lpcta_contact_view",
      metadata: {
        page: "contato",
      },
      dedupeKey: "page:lpcta_contato",
      dedupeWindowMs: 2000,
    });
  }, []);

  useEffect(() => {
    if (location.hash === "#agenda") {
      const agendaElement = document.getElementById("agenda");

      if (agendaElement) {
        setTimeout(() => {
          agendaElement.scrollIntoView({ behavior: "smooth", block: "start" });
        }, 100);
      }
    }
  }, [location.hash]);

  const handleChange = (event) => {
    const { name, value } = event.target;
    setFormData((current) => ({
      ...current,
      [name]: value,
    }));
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    setSubmitted(true);

    if (!formData.name || !formData.email || !formData.whatsapp) {
      return;
    }

    const leadPayload = {
      nome: formData.name,
      email: formData.email,
      telefone: formData.whatsapp,
      observacao: "Lead capturado pela ContactPage da LP CTA para revendedores.",
      aceitouTermos: null,
    };

    const leadRequest = submitLeadContact(leadPayload);

    trackCRMEvent({
      eventType: "form_submit",
      eventName: "lpcta_contact_submit",
      metadata: {
        page: "contato",
        name: formData.name,
        email: formData.email,
        whatsapp: formData.whatsapp,
      },
    });

    await leadRequest;

    window.open(
      `https://api.whatsapp.com/send?phone=${CONTACT_WHATSAPP}&text=${whatsappMessage}`,
      "_blank",
      "noopener,noreferrer"
    );
  };

  const handleOpenSchedule = () => {
    trackCRMEvent({
      eventType: "cta_click",
      eventName: "lpcta_open_schedule",
      metadata: {
        page: "contato",
      },
    });
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-purple-50 text-gray-900">
      <header className="border-b bg-white/90 shadow-sm backdrop-blur">
        <div className="mx-auto flex max-w-7xl items-center justify-between px-4 py-4 sm:px-6 lg:px-8">
          <Button
            variant="ghost"
            onClick={() => navigate("/lp-cta")}
            className="text-gray-700 hover:bg-gray-100"
          >
            <ArrowLeft className="h-4 w-4" />
            Voltar
          </Button>

          <p className="text-sm font-semibold uppercase tracking-[0.22em] text-blue-600">
            Comece agora
          </p>
        </div>
      </header>

      <main className="mx-auto max-w-7xl px-4 py-12 sm:px-6 lg:px-8 lg:py-16">
        <div className="grid gap-8 lg:grid-cols-[0.95fr_1.05fr]">
          <Card className="border-0 bg-white shadow-xl">
            <CardHeader>
              <p className="text-sm font-semibold uppercase tracking-[0.2em] text-blue-600">
                Formulário principal
              </p>
              <CardTitle className="text-4xl font-black text-gray-900">
                Comece agora
              </CardTitle>
              <CardDescription className="text-base leading-7 text-gray-600">
                Preencha seus dados e fale com nossa equipe.
              </CardDescription>
            </CardHeader>

            <CardContent>
              <form className="space-y-5" onSubmit={handleSubmit}>
                <div className="space-y-2">
                  <Label htmlFor="name">Nome</Label>
                  <Input
                    id="name"
                    name="name"
                    placeholder="Seu nome"
                    value={formData.name}
                    onChange={handleChange}
                    className="h-12 border-gray-200 bg-white"
                  />
                  {submitted && !formData.name ? (
                    <p className="text-sm text-red-500">Informe seu nome.</p>
                  ) : null}
                </div>

                <div className="space-y-2">
                  <Label htmlFor="email">Email</Label>
                  <Input
                    id="email"
                    name="email"
                    type="email"
                    placeholder="voce@empresa.com"
                    value={formData.email}
                    onChange={handleChange}
                    className="h-12 border-gray-200 bg-white"
                  />
                  {submitted && !formData.email ? (
                    <p className="text-sm text-red-500">Informe seu email.</p>
                  ) : null}
                </div>

                <div className="space-y-2">
                  <Label htmlFor="whatsapp">WhatsApp</Label>
                  <Input
                    id="whatsapp"
                    name="whatsapp"
                    placeholder="(00) 00000-0000"
                    value={formData.whatsapp}
                    onChange={handleChange}
                    className="h-12 border-gray-200 bg-white"
                  />
                  {submitted && !formData.whatsapp ? (
                    <p className="text-sm text-red-500">Informe seu WhatsApp.</p>
                  ) : null}
                </div>

                <Button
                  type="submit"
                  className="h-12 w-full bg-gradient-to-r from-blue-600 to-purple-600 text-white hover:from-blue-700 hover:to-purple-700"
                >
                  <MessageCircle className="h-4 w-4" />
                  Quero ganhar dinheiro revendendo
                </Button>
              </form>

              <div className="mt-6 grid gap-3 rounded-3xl bg-gradient-to-r from-blue-600 to-purple-600 p-5 text-white">
                {benefits.map((item) => (
                  <div key={item} className="flex items-center gap-3">
                    <CheckCircle2 className="h-5 w-5 text-yellow-300" />
                    <p className="text-sm text-white">{item}</p>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>

          <div className="space-y-8">
            <Card className="border-0 bg-gray-900 shadow-xl">
              <CardContent className="p-8 text-white">
                <p className="text-sm font-semibold uppercase tracking-[0.2em] text-yellow-300">
                  Oportunidade
                </p>
                <h2 className="mt-3 text-3xl font-black">
                  Ganhe dinheiro todos os meses com uma operação simples de revenda.
                </h2>
                <p className="mt-4 text-base leading-7 text-gray-300">
                  Aqui você entra em contato com a equipe, entende o modelo e
                  avança rapidamente para começar.
                </p>

                <div className="mt-6 grid gap-3">
                  <div className="flex items-center gap-3 rounded-2xl bg-white/10 px-4 py-3">
                    <ShieldCheck className="h-5 w-5 text-yellow-300" />
                    <p className="text-sm font-semibold">
                      Sem investimento inicial
                    </p>
                  </div>
                  <div className="flex items-center gap-3 rounded-2xl bg-white/10 px-4 py-3">
                    <Mail className="h-5 w-5 text-yellow-300" />
                    <p className="text-sm font-semibold">
                      Resposta comercial rápida
                    </p>
                  </div>
                  <div className="flex items-center gap-3 rounded-2xl bg-white/10 px-4 py-3">
                    <Phone className="h-5 w-5 text-yellow-300" />
                    <p className="text-sm font-semibold">
                      Contato direto pelo WhatsApp
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card id="agenda" className="border-0 bg-white shadow-xl">
              <CardHeader>
                <CardTitle className="text-2xl font-black text-gray-900">
                  Prefere uma apresentação?
                </CardTitle>
                <CardDescription className="text-base leading-7 text-gray-600">
                  Agende um horário e veja como funciona na prática.
                </CardDescription>
              </CardHeader>

              <CardContent>
                {GOOGLE_CALENDAR_EMBED_URL ? (
                  <div className="overflow-hidden rounded-3xl border border-gray-200">
                    <iframe
                      src={GOOGLE_CALENDAR_EMBED_URL}
                      title="Agenda de apresentações"
                      className="h-[520px] w-full"
                    />
                  </div>
                ) : (
                  <div className="flex h-[280px] items-center justify-center rounded-3xl border border-dashed border-blue-200 bg-gradient-to-br from-blue-50 to-purple-50 p-8 text-center">
                    <div>
                      <CalendarDays className="mx-auto h-12 w-12 text-purple-600" />
                      <p className="mt-4 text-lg font-semibold text-gray-900">
                        Agenda pronta para receber seus horários
                      </p>
                      <p className="mt-2 max-w-md text-sm leading-6 text-gray-500">
                        Informe seu link público do Google Agenda para ativar o agendamento.
                      </p>
                    </div>
                  </div>
                )}

                <div className="mt-6">
                  <Button
                    asChild
                    className="w-full bg-gradient-to-r from-blue-600 to-purple-600 text-white hover:from-blue-700 hover:to-purple-700"
                  >
                    <a
                      href={GOOGLE_CALENDAR_LINK || "https://calendar.google.com/"}
                      target="_blank"
                      rel="noopener noreferrer"
                      onClick={handleOpenSchedule}
                    >
                      <CalendarDays className="h-4 w-4" />
                      Agendar apresentação
                    </a>
                  </Button>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </main>
    </div>
  );
};

export default ContactPage;
